import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

export interface PortfolioHoldingsParams {
  investor: string;
  lang: Lang;
}

const portfolioHoldingsSchema = z.object({
  data: z.array(
    z.object({
      // 季度，如 2018Q2
      quarter: z.string().nullish(),
      // 標的代碼，如 KO
      symbol: z.string().nullish(),
      // 佔比，如 0.5
      proportion: z.number().nullish(),
    })
  ),
  message: z.string(),
});

export type PortfolioHoldingsData = z.infer<
  typeof portfolioHoldingsSchema
>["data"][number];

export const getInvestorPortfolioHoldings = async ({
  investor,
  lang,
}: PortfolioHoldingsParams): Promise<PortfolioHoldingsData[]> => {
  const endpoint = `/13f/${investor}/portfolio-distribution/holding-ratio`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return portfolioHoldingsSchema.parse(await res.json()).data;
};

export const useInvestorPortfolioHoldings = ({
  investor,
  enabled = false,
}: {
  investor: string;
  enabled?: boolean;
}) => {
  const { lang } = useLang();
  return useQuery(
    ["13f-investor-portfolio-holdings", investor, lang],
    () => getInvestorPortfolioHoldings({ investor, lang }),
    { enabled }
  );
};
